import { useState } from "react";
import { KeyRound, MailCheck, X } from "lucide-react";
import { api, C, errorMessage } from "../lib/api";

// "Forgot your sign-in details": the player gives the email they signed up with and we email a one-time link.
export const ForgotPasswordDialog = ({ onClose }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(C.ApiRoutes.AUTH_FORGOT_PASSWORD, { email: email.trim() });
      setSent(data);
    } catch (ex) {
      const code = ex?.response?.status;
      setErr(code === 404
        ? "We can't find an account with that email. Please check the address and try again, or email support@playersroom.au"
        : code === 429 ? "Too many attempts — please wait 15 minutes and try again." : errorMessage(ex));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-4" onClick={onClose} data-testid="forgot-dialog">
      <div onClick={(e) => e.stopPropagation()} className="glass rounded-3xl p-7 w-full max-w-md flex flex-col gap-4 rise">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] gold-text">Players Room</div>
            <h2 className="text-lg font-display mt-1 flex items-center gap-2"><KeyRound className="w-4 h-4 text-amber-400" /> Forgot your sign-in details</h2>
          </div>
          <button data-testid="forgot-close" onClick={onClose} className="text-slate-400 hover:text-amber-200"><X className="w-5 h-5" /></button>
        </div>

        {sent ? (
          <div className="flex flex-col gap-4" data-testid="forgot-sent">
            <div className="rounded-2xl border border-emerald-400/30 bg-emerald-400/5 px-4 py-4 text-sm text-emerald-100 flex gap-3">
              <MailCheck className="w-5 h-5 shrink-0 text-emerald-300" />
              <div>
                {sent.status === "sent"
                  ? <>Check <strong>{sent.email}</strong> for an email from Players Room and click the link to choose a new password. It works once and expires in {sent.expires_minutes} minutes.</>
                  : <>Your account was found, but the room's email isn't switched on yet, so the link could not be sent. Email <strong>support@playersroom.au</strong> and the host will set you a new password.</>}
              </div>
            </div>
            <p className="text-xs text-slate-400">Nothing in your inbox after a few minutes? Check the junk folder, then try again.</p>
            <button data-testid="forgot-done" onClick={onClose} className="rounded-full py-3 font-display text-sm bg-amber-500 hover:bg-amber-400 text-slate-900 btn-lift">DISMISS</button>
          </div>
        ) : (
          <form onSubmit={submit} className="flex flex-col gap-4">
            <p className="text-sm text-slate-300">Enter the email you signed up with. We'll send you a link to set a new password — the link works once and lasts an hour.</p>
            <label className="flex flex-col gap-1 text-xs text-slate-400">Email
              <input data-testid="forgot-email" type="email" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)}
                className="bg-black/40 border border-white/10 rounded-lg px-3 py-3 text-base text-slate-100 focus:border-amber-400 outline-none" />
            </label>
            {err && <div data-testid="forgot-error" className="text-sm text-red-200 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{err}</div>}
            <button data-testid="forgot-submit" disabled={busy || !email.trim()} className="rounded-full py-3 font-display text-sm bg-amber-500 hover:bg-amber-400 text-slate-900 btn-lift disabled:opacity-50">{busy ? "…" : "SEND ME A LINK"}</button>
          </form>
        )}
      </div>
    </div>
  );
};
